import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from './store'
import { firebaseLogout, getIdToken, waitForAuthStateReady } from './firebase'
import { letBrowserLabelUploads } from './uploadContentType'

type RuntimeImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>
}

const runtimeEnv = (import.meta as RuntimeImportMeta).env
const API_URL =
  runtimeEnv?.VITE_API_URL || (runtimeEnv?.DEV ? 'http://localhost:8000' : '')

if (!API_URL && runtimeEnv?.PROD) {
  console.error('FATAL: VITE_API_URL environment variable is not set')
}

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const AUTH_BOOTSTRAP_WAIT_MS = 2000

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retriedWithFreshToken?: boolean
  _usesExplicitAuthHeader?: boolean
}

function getAuthorizationHeader(
  config: InternalAxiosRequestConfig | undefined
): string | undefined {
  if (!config?.headers) return undefined

  const headers = config.headers as {
    get?: (name: string) => unknown
    Authorization?: unknown
    authorization?: unknown
    AUTHORIZATION?: unknown
  }

  if (typeof headers.get === 'function') {
    const viaGetter = headers.get('Authorization')
    if (typeof viaGetter === 'string' && viaGetter.trim()) {
      return viaGetter
    }
  }

  const direct =
    headers.Authorization ??
    headers.authorization ??
    headers.AUTHORIZATION

  if (typeof direct === 'string' && direct.trim()) {
    return direct
  }

  return undefined
}

/**
 * Get a Firebase ID token (auto-refreshed by Firebase SDK).
 * Falls back to accessToken for impersonation sessions.
 */
const getAuthToken = async (): Promise<string | null> => {
  const { isImpersonating, accessToken, isAuthenticated } = useAuthStore.getState()

  // Impersonation uses a raw access token
  if (isImpersonating && accessToken) {
    return accessToken
  }

  // On app startup, persisted auth state can be ready before Firebase restores
  // auth.currentUser. Wait briefly to avoid sending anonymous bootstrap calls.
  if (isAuthenticated) {
    await waitForAuthStateReady(AUTH_BOOTSTRAP_WAIT_MS)
  }

  // Firebase auth — get fresh ID token (auto-refreshed)
  return await getIdToken()
}

const clearFirebaseSession = async (): Promise<void> => {
  try {
    await firebaseLogout()
  } catch {
    // Continue clearing local auth state even if Firebase sign-out fails.
  }
  useAuthStore.getState().logout()
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    letBrowserLabelUploads(config)
    const requestConfig = config as RetriableRequestConfig
    // Preserve the origin of auth across a retry. Axios carries our injected
    // header forward too; treating that as caller-supplied auth would bypass
    // session cleanup if the refreshed token is rejected again.
    const hasExplicitAuthHeader = requestConfig._usesExplicitAuthHeader ??
      Boolean(getAuthorizationHeader(config))
    requestConfig._usesExplicitAuthHeader = hasExplicitAuthHeader

    // Respect explicit Authorization headers supplied by callers
    // (e.g. support/backdoor tenant context tokens).
    if (hasExplicitAuthHeader ||
        (requestConfig._retriedWithFreshToken && getAuthorizationHeader(config))) {
      return config
    }

    const token = await getAuthToken()
    if (token) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — on 401, logout and redirect
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { isImpersonating, isAuthenticated } = useAuthStore.getState()
    const originalRequest = error.config as RetriableRequestConfig | undefined
    const requestUsedExplicitAuth = Boolean(
      originalRequest?._usesExplicitAuthHeader
    )

    if (error.response?.status === 403) {
      const respData = error.response.data as { detail?: { code?: string } | string; code?: string } | undefined
      const errCode = (typeof respData?.detail === 'object' ? respData.detail?.code : undefined) || respData?.code
      if (errCode === 'DEMO_READ_ONLY') {
        window.dispatchEvent(
          new CustomEvent('lipaflow:demo-action-blocked', {
            detail: {
              message:
                typeof respData?.detail === 'string'
                  ? respData.detail
                  : 'Action not allowed in demo preview.',
            },
          })
        )
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401) {
      // For explicit per-request auth (tenant support/backdoor tokens),
      // bubble up 401 to caller. Do not refresh/login-switch globally.
      if (requestUsedExplicitAuth) {
        return Promise.reject(error)
      }

      // Impersonation sessions — end impersonation and redirect
      if (isImpersonating) {
        useAuthStore.getState().stopImpersonation()
        window.location.href = '/'
        return Promise.reject(error)
      }

      if (
        isAuthenticated &&
        originalRequest &&
        !originalRequest._retriedWithFreshToken
      ) {
        originalRequest._retriedWithFreshToken = true

        try {
          await waitForAuthStateReady(AUTH_BOOTSTRAP_WAIT_MS)
          const refreshedToken = await getIdToken(true)
          if (refreshedToken) {
            originalRequest.headers = originalRequest.headers ?? {}
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`
            return api.request(originalRequest)
          }
        } catch {
          // Fall through to logout when token refresh fails.
        }
      }

      // Firebase tokens are auto-refreshed, so a 401 means the session
      // is invalid (user disabled, claims stale, etc.)
      await clearFirebaseSession()
      // Already signing in: reloading would drop what the page was asked to
      // do (?method=phone after signing out to approve) for nothing. A
      // request still in flight when the session ended lands here.
      if (!window.location.pathname.startsWith('/login')) window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default api
